// src/pages/AdminEmpresaClientesCreate.jsx

import React, { useMemo, useState } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";

import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Grid,
  Paper,
  Stack,
  Divider,
  Chip,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import BadgeIcon from "@mui/icons-material/Badge";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import HomeIcon from "@mui/icons-material/Home";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import PaymentIcon from "@mui/icons-material/Payment";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";

import { db } from "../contexts/firebase/config";
import { collection, addDoc, Timestamp } from "firebase/firestore";

function SectionCard({ icon, title, subtitle, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 4,
        border: (t) => `1px solid ${t.palette.divider}`,
        bgcolor: (t) =>
          t.palette.mode === "dark"
            ? "rgba(255,255,255,0.03)"
            : "rgba(255,255,255,0.72)",
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: 3,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(99,102,241,0.12)",
            color: "primary.main",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ width: "100%" }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {title}
          </Typography>

          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {subtitle}
            </Typography>
          )}

          {children}
        </Box>
      </Stack>
    </Paper>
  );
}

function formatarDataInput(date) {
  if (!date || isNaN(date)) return "";
  return date.toISOString().split("T")[0];
}

function calcularProximaRenovacao(dataUltimaRenovacao, plano, modalidade) {
  if (plano === "Expirado") return null;
  if (!dataUltimaRenovacao) return null;

  const dataBase = new Date(dataUltimaRenovacao);
  if (isNaN(dataBase)) return null;

  const proxima = new Date(dataBase);

  if (modalidade === "Trimestral" || plano === "PRO") {
    proxima.setMonth(proxima.getMonth() + 3);
  } else {
    proxima.setFullYear(proxima.getFullYear() + 1);
  }

  return proxima;
}

function calcularDiasRestantes(dataFim) {
  if (!dataFim || isNaN(dataFim)) return null;

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const fim = new Date(dataFim);
  fim.setHours(0, 0, 0, 0);

  return Math.ceil((fim - hoje) / (1000 * 60 * 60 * 24));
}

export default function AdminEmpresaClientesCreate() {
  const { empresaId } = useParams();
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    nome: "",
    email: "",
    nif: "",
    contacto: "",
    morada: "",
    plano: "Base",
    modalidade: "Anual",
    estado: "Novo",
    dataRegisto: "",
    dataUltimaRenovacao: "",

    notificarParceiroRenovacao: true,
    clienteRecebeReferenciaPagamento: false,
  });

  const planoExpirado = form.plano === "Expirado";

  const proximaRenovacao = useMemo(() => {
    return calcularProximaRenovacao(
      form.dataUltimaRenovacao,
      form.plano,
      form.modalidade
    );
  }, [form.dataUltimaRenovacao, form.plano, form.modalidade]);

  const diasRestantes = useMemo(() => {
    return calcularDiasRestantes(proximaRenovacao);
  }, [proximaRenovacao]);

  const estadoRenovacao = useMemo(() => {
    if (planoExpirado) return "Expirado";
    if (diasRestantes === null) return "Sem data";
    if (diasRestantes < 0) return "Expirado";
    if (diasRestantes <= 15) return "Urgente";
    if (diasRestantes <= 30) return "A renovar";
    return "Ativo";
  }, [diasRestantes, planoExpirado]);

  const corEstadoRenovacao = useMemo(() => {
    if (estadoRenovacao === "Expirado") return "error";
    if (estadoRenovacao === "Urgente") return "error";
    if (estadoRenovacao === "A renovar") return "warning";
    if (estadoRenovacao === "Ativo") return "success";
    return "default";
  }, [estadoRenovacao]);

  const handleChange = (field, value) => {
    setForm((prev) => {
      if (field === "plano" && value === "Expirado") {
        return {
          ...prev,
          plano: "Expirado",
          modalidade: "",
          estado: "Expirado",
          dataRegisto: "",
          dataUltimaRenovacao: "",
        };
      }

      if (
        field === "plano" &&
        prev.plano === "Expirado" &&
        ["Base", "Flex", "PRO"].includes(value)
      ) {
        return {
          ...prev,
          plano: value,
          modalidade: value === "PRO" ? "Trimestral" : "Anual",
          estado: "Novo",
        };
      }

      return {
        ...prev,
        [field]: value,
      };
    });
  };

  async function handleSubmit(e) {
    e.preventDefault();

    if (!form.nome.trim()) return;

    try {
      setSaving(true);

      const clienteData = {
        empresaId,

        nome: form.nome,
        email: form.email,
        nif: form.nif,
        contacto: form.contacto,
        morada: form.morada,

        plano: form.plano,
        modalidade: form.plano === "Expirado" ? null : form.modalidade,
        estado: form.plano === "Expirado" ? "Expirado" : form.estado,

        aviso30Enviado: false,
        aviso15Enviado: false,
        aviso5Enviado: false,
        aviso0Enviado: false,

        notificarParceiroRenovacao: form.notificarParceiroRenovacao,
        clienteRecebeReferenciaPagamento: form.clienteRecebeReferenciaPagamento,

        createdAt: Timestamp.now(),
      };

      if (form.plano !== "Expirado") {
        const dataRegisto = form.dataRegisto
          ? new Date(form.dataRegisto)
          : new Date();

        const dataUltimaRenovacao = form.dataUltimaRenovacao
          ? new Date(form.dataUltimaRenovacao)
          : new Date();

        const dataFim = calcularProximaRenovacao(
          dataUltimaRenovacao,
          form.plano,
          form.modalidade
        );

        clienteData.dataRegisto = Timestamp.fromDate(dataRegisto);
        clienteData.dataUltimaRenovacao =
          Timestamp.fromDate(dataUltimaRenovacao);
        clienteData.dataFim = Timestamp.fromDate(dataFim);
      }

      await addDoc(collection(db, "clientes"), clienteData);

      navigate(`/admin/empresa/${empresaId}/clientes`);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: { xs: 2, md: 4 },
        background:
          "linear-gradient(135deg, #eef2ff 0%, #f8fafc 35%, #ffffff 100%)",
      }}
    >
      <Box sx={{ maxWidth: 1500, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            overflow: "hidden",
            borderRadius: 6,
            background:
              "linear-gradient(135deg,#0f172a 0%, #14213d 55%, #312e81 100%)",
            color: "white",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 24px 60px rgba(15,23,42,0.28)",
          }}
        >
          <Grid container>
            <Grid item xs={12} lg={9}>
              <Box sx={{ p: { xs: 3, md: 5 } }}>
                <Chip
                  icon={<Inventory2Icon />}
                  label="EMPRESA · CLIENTES"
                  sx={{
                    mb: 2,
                    bgcolor: "rgba(255,255,255,0.08)",
                    color: "white",
                    fontWeight: 700,
                  }}
                />

                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 900,
                    lineHeight: 1,
                    mb: 2,
                    letterSpacing: "-0.04em",
                    fontSize: {
                      xs: "2.6rem",
                      md: "4rem",
                    },
                  }}
                >
                  Adicionar novo cliente
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    color: "rgba(255,255,255,0.72)",
                    maxWidth: 700,
                    lineHeight: 1.7,
                    fontWeight: 400,
                  }}
                >
                  Preenche os dados do cliente. A data de registo fica apenas
                  guardada como histórico; a renovação é calculada pela última
                  renovação.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} lg={3}>
              <Box
                sx={{
                  p: 4,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Button
                  component={RouterLink}
                  to={`/admin/empresa/${empresaId}/clientes`}
                  startIcon={<ArrowBackIcon />}
                  variant="outlined"
                  fullWidth
                  sx={{
                    borderRadius: 999,
                    color: "white",
                    borderColor: "rgba(255,255,255,0.2)",
                    backdropFilter: "blur(10px)",
                    bgcolor: "rgba(255,255,255,0.06)",
                    "&:hover": {
                      borderColor: "rgba(255,255,255,0.3)",
                      bgcolor: "rgba(255,255,255,0.10)",
                    },
                  }}
                >
                  Voltar
                </Button>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 4,
                    bgcolor: "rgba(255,255,255,0.75)",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "text.secondary",
                      fontWeight: 700,
                    }}
                  >
                    Plano selecionado
                  </Typography>

                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 900,
                      color: "#111827",
                    }}
                  >
                    {form.plano}
                    {!planoExpirado && ` · ${form.modalidade}`}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      color: planoExpirado ? "#dc2626" : "#334155",
                      fontWeight: 700,
                    }}
                  >
                    Próxima renovação:{" "}
                    {proximaRenovacao && !planoExpirado
                      ? proximaRenovacao.toLocaleDateString("pt-PT")
                      : "—"}
                  </Typography>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={3}>
          <Grid item xs={12} lg={5}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 5,
                height: "100%",
                border: (t) => `1px solid ${t.palette.divider}`,
                bgcolor: "rgba(255,255,255,0.72)",
                backdropFilter: "blur(14px)",
              }}
            >
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: 4,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "rgba(99,102,241,0.12)",
                  color: "primary.main",
                  mb: 3,
                }}
              >
                <PersonAddAlt1Icon />
              </Box>

              <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
                Novo registo
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                A data de registo serve apenas para histórico. O sistema usa a
                última renovação para calcular a próxima renovação e os emails
                automáticos.
              </Typography>

              <Divider sx={{ mb: 3 }} />

              <Stack spacing={2}>
                <Chip
                  icon={<CalendarMonthIcon />}
                  label="Data de registo: apenas histórico"
                  sx={{
                    width: "fit-content",
                    fontWeight: 700,
                  }}
                />

                <Chip
                  icon={<WorkspacePremiumIcon />}
                  label={
                    planoExpirado
                      ? "Plano expirado: datas desativadas"
                      : "Última renovação: calcula a próxima renovação"
                  }
                  color={planoExpirado ? "error" : "primary"}
                  sx={{
                    width: "fit-content",
                    fontWeight: 700,
                  }}
                />

                <Chip
                  label={
                    planoExpirado
                      ? "Cliente marcado como expirado"
                      : diasRestantes === null
                      ? "Sem data de renovação"
                      : diasRestantes < 0
                      ? `Expirado há ${Math.abs(diasRestantes)} dia(s)`
                      : `Faltam ${diasRestantes} dia(s)`
                  }
                  color={corEstadoRenovacao}
                  sx={{
                    width: "fit-content",
                    fontWeight: 800,
                  }}
                />

                <Chip
                  icon={
                    form.notificarParceiroRenovacao ? (
                      <NotificationsActiveIcon />
                    ) : (
                      <NotificationsOffIcon />
                    )
                  }
                  label={
                    form.notificarParceiroRenovacao
                      ? "Parceiro será notificado nas renovações"
                      : "Parceiro não será notificado nas renovações"
                  }
                  color={form.notificarParceiroRenovacao ? "success" : "default"}
                  sx={{
                    width: "fit-content",
                    fontWeight: 800,
                  }}
                />

                <Chip
                  icon={
                    form.clienteRecebeReferenciaPagamento ? (
                      <PaymentIcon />
                    ) : (
                      <MoneyOffIcon />
                    )
                  }
                  label={
                    form.clienteRecebeReferenciaPagamento
                      ? "Cliente recebe email com referências"
                      : "Cliente recebe email sem referências"
                  }
                  color={
                    form.clienteRecebeReferenciaPagamento ? "warning" : "default"
                  }
                  sx={{
                    width: "fit-content",
                    fontWeight: 800,
                  }}
                />
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={7}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 5,
                border: (t) => `1px solid ${t.palette.divider}`,
                bgcolor: "rgba(255,255,255,0.72)",
                backdropFilter: "blur(14px)",
              }}
            >
              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  <SectionCard
                    icon={<PersonAddAlt1Icon />}
                    title="Dados do cliente"
                    subtitle="Informação principal do novo cliente."
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Nome *"
                          fullWidth
                          value={form.nome}
                          onChange={(e) =>
                            handleChange("nome", e.target.value)
                          }
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <BusinessCenterIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Email (opcional)"
                          fullWidth
                          value={form.email}
                          onChange={(e) =>
                            handleChange("email", e.target.value)
                          }
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </SectionCard>

                  <SectionCard
                    icon={<BadgeIcon />}
                    title="Identificação & contacto"
                    subtitle="Dados fiscais e contacto direto."
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="NIF"
                          fullWidth
                          value={form.nif}
                          onChange={(e) =>
                            handleChange("nif", e.target.value)
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Contacto"
                          fullWidth
                          value={form.contacto}
                          onChange={(e) =>
                            handleChange("contacto", e.target.value)
                          }
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIphoneIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Morada"
                          fullWidth
                          value={form.morada}
                          onChange={(e) =>
                            handleChange("morada", e.target.value)
                          }
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <HomeIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </SectionCard>

                  <SectionCard
                    icon={<WorkspacePremiumIcon />}
                    title="Plano & renovação"
                    subtitle={
                      planoExpirado
                        ? "Cliente marcado como expirado. As datas ficam desativadas até escolher Base, Flex ou PRO."
                        : "A data de registo é apenas histórica. O cálculo é feito pela última renovação."
                    }
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Data de Registo"
                          type="date"
                          fullWidth
                          disabled={planoExpirado}
                          InputLabelProps={{ shrink: true }}
                          value={form.dataRegisto}
                          onChange={(e) =>
                            handleChange("dataRegisto", e.target.value)
                          }
                          helperText={
                            planoExpirado
                              ? "Desativado para plano expirado."
                              : "Apenas histórico. Não calcula a renovação."
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Última Renovação"
                          type="date"
                          fullWidth
                          disabled={planoExpirado}
                          InputLabelProps={{ shrink: true }}
                          value={form.dataUltimaRenovacao}
                          onChange={(e) =>
                            handleChange("dataUltimaRenovacao", e.target.value)
                          }
                          helperText={
                            planoExpirado
                              ? "Desativado para plano expirado."
                              : "Esta data calcula a próxima renovação."
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Próxima Renovação"
                          type="date"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={
                            planoExpirado
                              ? ""
                              : formatarDataInput(proximaRenovacao)
                          }
                          disabled
                          helperText={
                            planoExpirado
                              ? "Sem renovação definida."
                              : "Calculado automaticamente."
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Dias até expirar"
                          fullWidth
                          value={
                            planoExpirado
                              ? "Expirado"
                              : diasRestantes === null
                              ? "—"
                              : diasRestantes < 0
                              ? `Expirado há ${Math.abs(diasRestantes)} dias`
                              : `${diasRestantes} dias`
                          }
                          disabled
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          select
                          label="Plano"
                          fullWidth
                          value={form.plano}
                          onChange={(e) =>
                            handleChange("plano", e.target.value)
                          }
                        >
                          <MenuItem value="Base">Base</MenuItem>
                          <MenuItem value="Flex">Flex</MenuItem>
                          <MenuItem value="PRO">PRO</MenuItem>
                          <MenuItem value="Experimental">Experimental</MenuItem>
                          <MenuItem value="Expirado">Expirado</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          select
                          label="Modalidade"
                          fullWidth
                          disabled={planoExpirado}
                          value={form.modalidade}
                          onChange={(e) =>
                            handleChange("modalidade", e.target.value)
                          }
                        >
                          <MenuItem value="Anual">Anual</MenuItem>
                          <MenuItem value="Trimestral">Trimestral</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={12} md={12}>
                        <TextField
                          select
                          label="Estado"
                          fullWidth
                          value={form.estado}
                          onChange={(e) =>
                            handleChange("estado", e.target.value)
                          }
                        >
                          <MenuItem value="Novo">Novo</MenuItem>
                          <MenuItem value="Renovação">Renovação</MenuItem>
                          <MenuItem value="Expirado">Expirado</MenuItem>
                        </TextField>
                      </Grid>
                    </Grid>
                  </SectionCard>

                  <SectionCard
                    icon={<EmailIcon />}
                    title="Emails automáticos de renovação"
                    subtitle="Estas opções ficam guardadas no cliente e serão usadas depois pelas Cloud Functions."
                  >
                    <Stack spacing={3}>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 900, mb: 1 }}
                        >
                          Notificação do parceiro
                        </Typography>

                        <ToggleButtonGroup
                          exclusive
                          fullWidth
                          value={
                            form.notificarParceiroRenovacao ? "sim" : "nao"
                          }
                          onChange={(event, value) => {
                            if (!value) return;
                            handleChange(
                              "notificarParceiroRenovacao",
                              value === "sim"
                            );
                          }}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "1fr 1fr",
                            },
                            gap: 1,
                            "& .MuiToggleButtonGroup-grouped": {
                              borderRadius: "16px !important",
                              border: (t) =>
                                `1px solid ${t.palette.divider} !important`,
                            },
                          }}
                        >
                          <ToggleButton
                            value="sim"
                            sx={{
                              p: 2,
                              textTransform: "none",
                              fontWeight: 800,
                              justifyContent: "flex-start",
                              gap: 1,
                            }}
                          >
                            <NotificationsActiveIcon />
                            Notificar parceiro
                          </ToggleButton>

                          <ToggleButton
                            value="nao"
                            sx={{
                              p: 2,
                              textTransform: "none",
                              fontWeight: 800,
                              justifyContent: "flex-start",
                              gap: 1,
                            }}
                          >
                            <NotificationsOffIcon />
                            Não notificar parceiro
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Box>

                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 900, mb: 1 }}
                        >
                          Referência de pagamento no email do cliente
                        </Typography>

                        <ToggleButtonGroup
                          exclusive
                          fullWidth
                          value={
                            form.clienteRecebeReferenciaPagamento
                              ? "comReferencia"
                              : "semReferencia"
                          }
                          onChange={(event, value) => {
                            if (!value) return;
                            handleChange(
                              "clienteRecebeReferenciaPagamento",
                              value === "comReferencia"
                            );
                          }}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "1fr 1fr",
                            },
                            gap: 1,
                            "& .MuiToggleButtonGroup-grouped": {
                              borderRadius: "16px !important",
                              border: (t) =>
                                `1px solid ${t.palette.divider} !important`,
                            },
                          }}
                        >
                          <ToggleButton
                            value="comReferencia"
                            sx={{
                              p: 2,
                              textTransform: "none",
                              fontWeight: 800,
                              justifyContent: "flex-start",
                              gap: 1,
                            }}
                          >
                            <PaymentIcon />
                            Cliente com referências
                          </ToggleButton>

                          <ToggleButton
                            value="semReferencia"
                            sx={{
                              p: 2,
                              textTransform: "none",
                              fontWeight: 800,
                              justifyContent: "flex-start",
                              gap: 1,
                            }}
                          >
                            <MoneyOffIcon />
                            Cliente sem referências
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                    </Stack>
                  </SectionCard>

                  <Divider />

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="flex-end"
                    spacing={2}
                  >
                    <Button
                      component={RouterLink}
                      to={`/admin/empresa/${empresaId}/clientes`}
                      sx={{
                        borderRadius: 999,
                        fontWeight: 700,
                        textTransform: "none",
                      }}
                    >
                      Cancelar
                    </Button>

                    <Button
                      type="submit"
                      variant="contained"
                      disabled={saving}
                      startIcon={<SaveIcon />}
                      sx={{
                        borderRadius: 999,
                        px: 3,
                        py: 1.2,
                        fontWeight: 800,
                        textTransform: "none",
                        boxShadow: "0 12px 30px rgba(99,102,241,0.35)",
                      }}
                    >
                      {saving ? "A guardar..." : "Salvar Cliente"}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}